import { useState, useEffect } from 'react';
import SearchBar from './components/SearchBar';
import FilterControls from './components/FilterControls';
import SortControls from './components/SortControls';
import EventCard from './components/EventCard';
import EmptyState from './components/EmptyState';
import Navbar from '../home/components/Navbar';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface Event {
  id: string;
  title: string;
  description: string | null;
  category: string;
  start_date: string;
  end_date: string | null;
  location_type: string;
  location_city: string | null;
  location_country: string | null;
  cover_image: string | null;
  price: number | null;
  currency: string | null;
  is_published: boolean;
  registrations?: { count: number }[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

export default function DiscoverPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [priceRange, setPriceRange] = useState('All');
  const [sortBy, setSortBy] = useState('date');
  const { user } = useAuth();
  const [registeredEventIds, setRegisteredEventIds] = useState<Set<string>>(new Set());

  // SEO: inject page meta + Schema.org
  useEffect(() => {
    const PAGE_TITLE = 'Discover Events – Find Free & Paid Events | EVENTSHOWCASE';
    const PAGE_DESC = 'Browse and discover thousands of free and paid events on EVENTSHOWCASE. Filter by category, location, and price to find the perfect event for you.';
    const PAGE_URL = `${SITE_URL}/discover`;
    const PAGE_IMG = 'https://public.readdy.ai/ai/img_res/b520e2fa0d2d16b4607924aed6900643.jpg';

    document.title = PAGE_TITLE;

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); const [a, v] = sel.replace('meta[', '').replace(']', '').split('='); el.setAttribute(a.trim(), v.replace(/"/g, '').trim()); document.head.appendChild(el); el.setAttribute('data-page-meta', 'true'); }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:image"]', 'content', PAGE_IMG);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', PAGE_TITLE);
    setMeta('meta[name="twitter:description"]', 'content', PAGE_DESC);
    setMeta('meta[name="twitter:image"]', 'content', PAGE_IMG);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Discover Events',
      description: 'Browse and discover free and paid events on EVENTSHOWCASE.',
      url: `${SITE_URL}/discover`,
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'EVENTSHOWCASE' },
    };

    const existing = document.getElementById('schema-discover');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-discover';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-discover');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (user) {
      fetchUserRegistrations();
    } else {
      setRegisteredEventIds(new Set());
    }
  }, [user]);

  const fetchUserRegistrations = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('registrations')
        .select('event_id')
        .eq('user_id', user.id);
      if (error) throw error;
      setRegisteredEventIds(new Set((data || []).map((r: any) => r.event_id)));
    } catch (error) {
      console.error('Error fetching user registrations:', error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          registrations(count)
        `)
        .eq('is_published', true)
        .gte('start_date', new Date().toISOString())
        .order('start_date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      searchQuery === '' ||
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === 'All' || event.category === selectedCategory;

    const matchesLocation =
      selectedLocation === 'All' ||
      event.location_city === selectedLocation ||
      event.location_country === selectedLocation;

    const matchesPrice =
      priceRange === 'All' ||
      (priceRange === 'Free' && (!event.price || event.price === 0)) ||
      (priceRange === 'Paid' && event.price && event.price > 0);

    return matchesSearch && matchesCategory && matchesLocation && matchesPrice;
  });

  const sortedEvents = [...filteredEvents].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
      case 'price-low':
        return (a.price || 0) - (b.price || 0);
      case 'price-high':
        return (b.price || 0) - (a.price || 0);
      case 'popular':
        const aCount = a.registrations?.[0]?.count || 0;
        const bCount = b.registrations?.[0]?.count || 0;
        return bCount - aCount;
      default:
        return 0;
    }
  });

  const categories = ['All', ...Array.from(new Set(events.map(e => e.category)))];
  const locations = [
    'All',
    ...Array.from(
      new Set(
        events
          .map(e => e.location_city || e.location_country)
          .filter(Boolean)
      )
    ),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-500 pt-28 pb-16 sm:pt-32 sm:pb-20 md:pt-36 md:pb-24">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=abstract%20geometric%20pattern%20modern%20minimalist%20design%20teal%20emerald%20cyan%20gradient%20background%20professional%20clean&width=1920&height=600&seq=discover-hero-bg&orientation=landscape')] opacity-10 bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
              Discover Amazing Events
            </h1>
            <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto px-4">
              Find and join events that match your interests
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-3xl mx-auto mb-6 sm:mb-8">
            <SearchBar value={searchQuery} onChange={setSearchQuery} />
          </div>

          {/* Filter Controls */}
          <div className="max-w-5xl mx-auto">
            <FilterControls
              categories={categories}
              locations={locations}
              selectedCategory={selectedCategory}
              selectedLocation={selectedLocation}
              priceRange={priceRange}
              onCategoryChange={setSelectedCategory}
              onLocationChange={setSelectedLocation}
              onPriceRangeChange={setPriceRange}
            />
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
        {/* Results Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              {filteredEvents.length} Events Found
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {selectedCategory !== 'All Categories' && `${selectedCategory} • `}
              {selectedLocation !== 'All Locations' && `${selectedLocation} • `}
              {priceRange !== 'All' && priceRange}
            </p>
          </div>
          <div className="w-full sm:w-auto">
            <SortControls value={sortBy} onChange={setSortBy} />
          </div>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 sm:py-20">
            <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 text-sm sm:text-base">Loading events...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredEvents.map((event) => (
              <EventCard key={event.id} event={event} isRegistered={registeredEventIds.has(event.id)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
